import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, ArrowRight, BarChart3, Shield, Users, Scan, CreditCard, FileCheck, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Footer } from '@/components/common/Footer';
import chartFlowLogo from '@/assets/chart-flow-logo.png';

const Index = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src={chartFlowLogo} alt="Chart-Flow Logo" className="w-8 h-8" />
            <span className="font-bold text-xl">Chart-Flow</span>
          </div>
          <Link to="/auth">
            <Button>
              Se connecter
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-8 shadow-elegant bg-white">
            <img src={chartFlowLogo} alt="Chart-Flow Logo" className="w-16 h-16" />
          </div>
          <Badge variant="secondary" className="mb-4">
            🚀 Solution complète pour les professionnels
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            La gestion d'inventaire
            <span className="bg-gradient-primary bg-clip-text text-transparent"> nouvelle génération</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Révolutionnez votre gestion avec Chart-Flow : scanner intelligent, analyses prédictives, 
            gestion d'équipe et conformité fiscale NF 525. Tout en un, sans complexité.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/auth">
              <Button size="lg" className="bg-gradient-primary hover:shadow-elegant transition-all duration-300">
                <Zap className="mr-2 h-5 w-5" />
                Essai gratuit 14 jours
              </Button>
            </Link>
            <Link to="/auth">
              <Button size="lg" variant="outline" className="border-primary/20 hover:bg-primary/5">
                Voir une démo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">
            ✓ Sans engagement • ✓ Toutes les fonctionnalités incluses • ✓ Support gratuit
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Pourquoi choisir Chart-Flow ?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              La seule solution qui combine simplicité d'usage et fonctionnalités professionnelles
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Scan className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>Scanner intelligent</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Scannez vos codes-barres directement depuis l'app. Ajout automatique des produits 
                  avec reconnaissance intelligente des références.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-success/20 transition-colors">
                  <Users className="w-6 h-6 text-success" />
                </div>
                <CardTitle>Gestion d'équipe</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Invitez jusqu'à 5 collaborateurs, définissez les rôles et permissions. 
                  Travaillez en équipe en toute sécurité.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-warning/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-warning/20 transition-colors">
                  <BarChart3 className="w-6 h-6 text-warning" />
                </div>
                <CardTitle>Analyses prédictives</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Tableaux de bord personnalisables, analyses de tendances et prévisions 
                  pour optimiser vos achats et ventes.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-destructive/20 transition-colors">
                  <CreditCard className="w-6 h-6 text-destructive" />
                </div>
                <CardTitle>Caisse intégrée</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Point de vente complet avec paiements par carte, gestion des retraits 
                  et synchronisation automatique des stocks.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <FileCheck className="w-6 h-6 text-blue-500" />
                </div>
                <CardTitle>Conformité NF 525</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Respect des obligations fiscales françaises avec certification automatique 
                  et archivage sécurisé des données.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card hover:shadow-elegant transition-all duration-300 group">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                  <Shield className="w-6 h-6 text-purple-500" />
                </div>
                <CardTitle>Sécurité maximale</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center">
                  Chiffrement de bout en bout, sauvegarde automatique et authentification 
                  multi-facteurs pour protéger vos données.
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          {/* Success metrics */}
          <div className="grid md:grid-cols-4 gap-8 text-center mb-16">
            <div className="p-6">
              <div className="text-3xl font-bold text-primary mb-2">98%</div>
              <div className="text-muted-foreground">Satisfaction client</div>
            </div>
            <div className="p-6">
              <div className="text-3xl font-bold text-success mb-2">-60%</div>
              <div className="text-muted-foreground">Temps de gestion</div>
            </div>
            <div className="p-6">
              <div className="text-3xl font-bold text-warning mb-2">+40%</div>
              <div className="text-muted-foreground">Efficacité opérationnelle</div>
            </div>
            <div className="p-6">
              <div className="text-3xl font-bold text-destructive mb-2">24/7</div>
              <div className="text-muted-foreground">Support disponible</div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-primary/5 rounded-2xl p-12">
            <Target className="w-16 h-16 mx-auto mb-6 text-primary" />
            <h3 className="text-2xl font-bold mb-4">Prêt à transformer votre gestion ?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Rejoignez les centaines d'entreprises qui font déjà confiance à Chart-Flow 
              pour optimiser leur gestion d'inventaire.
            </p>
            <Link to="/auth">
              <Button size="lg" className="bg-gradient-primary hover:shadow-elegant">
                <Zap className="mr-2 h-5 w-5" />
                Commencer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;